import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { HTTP_STATUS_OK, HTTP_STATUS_CREATED } from '@tmapy/beetlejuice-utils';

import {
  changeDeleteSuccessAlertVisibility,
  changeSubmitSuccessAlertVisibility,
} from '../../app/actions';
import { useHistoryAction } from '../../hooks';
import { QUERY_STATE } from '../../constants';

import { usePublishsetsRetrieveResource } from '../../openapi/operations/publishsetsRetrieve';
import { publishsetsUpdate } from '../../openapi/operations/publishsetsUpdate';
import { publishsetsCreate } from '../../openapi/operations/publishsetsCreate';
import { publishsetsDestroy } from '../../openapi/operations/publishsetsDestroy';
import { PublishsetUpdateRequest } from '../../openapi/types';

import { PUBLISHSET_FORM_TYPE } from '../constants';

import { PublishsetForm } from './PublishsetForm';

export const Publishset: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const publishsetId = id ? parseInt(id, 10) : null;
  const formType = publishsetId ? PUBLISHSET_FORM_TYPE.EDIT : PUBLISHSET_FORM_TYPE.NEW;
  const resource = usePublishsetsRetrieveResource(publishsetId ? { id: publishsetId } : null);
  const routeToPublishsetList = useHistoryAction('vydejPublishsetList');

  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [shouldLeaveAfterSuccess, setShouldLeaveAfterSuccess] = useState(false);

  // Přesměrování až po překreslení se stavem SUCCESS, jinak useBlocker ve formuláři
  // ještě vidí neuložené změny a zeptá se na opuštění stránky.
  useEffect(() => {
    if (shouldLeaveAfterSuccess && submitQueryState === QUERY_STATE.SUCCESS) {
      routeToPublishsetList();
    }
  }, [shouldLeaveAfterSuccess, submitQueryState, routeToPublishsetList]);

  const onSubmit = useCallback(
    async (request: PublishsetUpdateRequest) => {
      setSubmitQueryState(QUERY_STATE.WAITING);
      const result = publishsetId
        ? await publishsetsUpdate({ query: { id: publishsetId }, request })
        : await publishsetsCreate({ request });

      if (
        result.data &&
        (result.status === HTTP_STATUS_OK || result.status === HTTP_STATUS_CREATED)
      ) {
        setSubmitQueryState(QUERY_STATE.SUCCESS);
        if (!resource.data?.id) {
          dispatch(changeSubmitSuccessAlertVisibility(true));
          setShouldLeaveAfterSuccess(true);
        }
      } else {
        setSubmitQueryState(QUERY_STATE.FAIL);
      }
      return result;
    },
    [publishsetId, resource.data?.id, dispatch],
  );

  const onDelete = useCallback(async () => {
    setDeleteQueryState(QUERY_STATE.WAITING);
    const response = await publishsetsDestroy({ query: { id: publishsetId! } });
    if (response.ok) {
      setSubmitQueryState(QUERY_STATE.SUCCESS);
      dispatch(changeDeleteSuccessAlertVisibility(true));
      setShouldLeaveAfterSuccess(true);
    } else {
      setSubmitQueryState(QUERY_STATE.FAIL);
    }
    return response;
  }, [publishsetId, dispatch]);

  return (
    <PublishsetForm
      key={`${resource.data?.id}-${resource.fetchedAt}`}
      formType={formType}
      resource={resource}
      submitQueryState={submitQueryState}
      onSubmit={onSubmit}
      deleteQueryState={deleteQueryState}
      onDelete={onDelete}
    />
  );
};
